import { forwardRef, useRef, useState, useEffect, useCallback } from 'react';
import './index.less'
import { useSelector } from "react-redux";
import dataJson from "@/../public/data.json";
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import { useGetLoginInfo } from "@/hooks/useGetLoginInfo";
import { 
  handlePubQuestionComment, 
  handlePubFaqComment
 } from '@/api/questions';
 import Tinymce from "@/components/Tinymce";
 import UploadImage from "@/components/UploadImage";
 import useTinymce from '@/hooks/useTinymce';

interface IProp {
  className?: string;
  type?: "faq" | "notice" | "feedback";
  questionId?: string;
  parentId?: string;
  placeholder?: string;
  hideAvatar?: boolean;
  afterSend?: (questionId: string, parentId: string) => void;
}

// eslint-disable-next-line react/display-name
const CommentInput = forwardRef((props: IProp, ref: any) => {
  const { className, type, questionId, parentId, placeholder = '分享你的技术经验', hideAvatar = false, afterSend } = props;
  const { avatar_icon } = dataJson;
  const [isFocus, setIsFocus] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  const { authorization, portalAuthorization, goLogin } = useGetLoginInfo();
  const userData = useSelector((state: any) => state.userData);
  const isAdmin = userData.nickName === '社区小助手';
  const avatar = userData.avatar || avatar_icon;
  const [value, setValue] = useState('');

  const {
    richValue,
    descriptionRef,
    uploadImageVisible,
    totalImageSize,
    setRichValue,
    onDesChange,
    onUploadImageCancel,
    addUploadImageButton,
    insertImage
  } = useTinymce()

  const handleEmojiSelect = (emoji: any) => {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const newText = value.slice(0, start) + emoji.native + value.slice(end);
    setValue(newText);
    setTimeout(() => {
      el.selectionStart = el.selectionEnd = start + emoji.native.length;
    }, 0);
  };

  // Shift+Enter换行，Enter发送
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSend = async () => {
    if (!isAdmin && !value) {
      return;
    }
    if (isAdmin && !richValue) {
      return;
    }
    const typeMap = {
      faq: handlePubFaqComment,
      feedback: handlePubQuestionComment
    }
    const API = typeMap[type as keyof typeof typeMap];
    if (API) {
      const res = await API({
        parentId: parentId || '',
        content: isAdmin ? richValue : value,
        questionId: questionId
      });
      if (res.code === 200) {
        window.message?.success('评论成功',2);
        setIsFocus(false);
        if (isAdmin) {
          setRichValue('')
          descriptionRef.current.tinymceContainer.current.setContent('');
        } else {
          setValue('');
        }
        afterSend && afterSend(questionId || '', parentId || '');
      } else {
        window.message?.error(res.message,2);
      }
    }
  }
  
  useEffect(() => {
    const documentClick = (e: any) => {
      if (e.target.className.startsWith('tox')) {
        return
      }
      setIsFocus(false)
    }
    document.addEventListener('click', documentClick)
    return () => {
      document.removeEventListener('click', documentClick)
    }
  }, [])
  return <div 
      className={`comment-input-wrap ${className} ${hideAvatar ? 'hideAvatar' : ''}`} 
      ref={ref} 
      style={{alignItems: isFocus ? 'flex-start' : 'center'}}
    >
    {
      !hideAvatar && <div className="comment-avatar">
        <img src={avatar} alt="" />
      </div>
    }
    <div className={`comment-content ${!isFocus && 'comment-content-normal'} ${isAdmin ? 'comment-content-admin' : ''}`} onClick={(e) => e.stopPropagation()}>
      {(!isAdmin || isAdmin && !isFocus) && <textarea
        ref={textareaRef}
        className="comment-content-textarea"
        value={value}
        placeholder={placeholder}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={(e) => {
          if (!(portalAuthorization && authorization)) {
            goLogin();
            return;
          }
          e.stopPropagation();
          setIsFocus(true);
          setShowEmoji(false);
        }}
      />}
      {
        isAdmin && <div style={{display: isFocus ? 'block' : 'none'}}>
          <Tinymce 
            ref={descriptionRef}
            value={richValue}
            height="500px"
            changeValue={onDesChange}
            onInit={addUploadImageButton}
          />
          <UploadImage 
            open={uploadImageVisible}
            onCancel={onUploadImageCancel}
            onOk={insertImage}
            totalSize={totalImageSize}
          />
        </div>
      }
      <div className='comment-content-toolbar'>
        <div className='tools'>
          {!isAdmin && <img src={require('@/assets/images/emoji.png')} style={{cursor: 'pointer'}} alt="" onClick={() => setShowEmoji(!showEmoji)} />}
        </div>
        <div className={`pub-btn ${value || richValue ? '' : 'disabled'}`} onClick={handleSend}>发布</div>
      </div>
      {showEmoji && !isAdmin && (
        <div className="emoji-panel">
          <Picker
            data={data}
            onEmojiSelect={handleEmojiSelect}
            theme="light"
            locale="zh"
          />
        </div>
      )}
    </div>
  </div>
})

export default CommentInput;